import {
  Activity,
  Coins,
  LineChart,
  LucideIcon,
  TrendingUp,
} from "lucide-react";
import PerformanceStatsSkeleton from "src/components/skeleton/PerformanceStatsSkeleton";

export interface IndexPerformanceItem {
  symbol: string;
  label: string;
  return_pct: number | null;
}

interface PerformanceStatsProps {
  header?: {
    market_performance: number;
    no_of_stock: number;
    avg_peak_return: number;
    avg_current_return: number;
  };
  indexPerformance?: IndexPerformanceItem[];
  isIndexLoading?: boolean;
  isLoading?: boolean;
  layout?: "row" | "col";
}

const formatPct = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value)
    ? "—"
    : `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

const toneClass = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "text-gray-400 dark:text-gray-500";
  }
  return value >= 0
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-rose-600 dark:text-rose-400";
};

/**
 * One slim strip or vertical side card for performance metrics.
 */
const Segment = ({
  icon: Icon,
  iconClass,
  label,
  children,
  grow = false,
}: {
  icon: LucideIcon;
  iconClass: string;
  label: string;
  children: React.ReactNode;
  grow?: boolean;
}) => (
  <div
    className={`flex min-w-0 items-center gap-2 px-2 py-1.5 ${
      grow ? "flex-[1.4]" : "flex-1"
    }`}
  >
    <span
      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md ${iconClass}`}
    >
      <Icon size={13} />
    </span>
    <div className="flex min-w-0 flex-col justify-center">
      <span className="truncate text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      {children}
    </div>
  </div>
);

const PerformanceStats: React.FC<PerformanceStatsProps> = ({
  header,
  indexPerformance = [],
  isIndexLoading = false,
  isLoading = false,
  layout = "row",
}) => {
  // Only the very first load (no header yet) gets the full skeleton — a
  // refetch triggered by a filter change keeps the previous numbers on
  // screen (dimmed below) instead of swapping in a differently-shaped
  // placeholder, which is what made the strip visibly jump before.
  if (isLoading && !header) {
    return <PerformanceStatsSkeleton />;
  }

  const nifty = indexPerformance.find((item) => item.symbol === "NIFTY 50");
  const bankNifty = indexPerformance.find(
    (item) => item.symbol === "BANK NIFTY",
  );

  return (
    <div
      className={`flex transition-opacity duration-200 ${
        layout === "col"
          ? "flex-col divide-y h-full justify-between"
          : "flex-wrap divide-x"
      } ${isLoading ? "opacity-60" : "opacity-100"} divide-[#d0d4e4] overflow-hidden bg-white dark:divide-[#394564] dark:bg-[#121419]`}
    >
      <Segment
        icon={LineChart}
        iconClass="bg-sky-500/15 text-sky-600 dark:text-sky-400"
        label="Nifty Performance"
        grow
      >
        <div className="flex items-baseline gap-3">
          {[
            { key: "nifty", item: nifty, fallback: "Nifty 50" },
            { key: "bank", item: bankNifty, fallback: "Bank Nifty" },
          ].map(({ key, item, fallback }) => (
            <span
              key={key}
              className="flex items-baseline gap-1 whitespace-nowrap"
            >
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                {item?.label ?? fallback}
              </span>
              {isIndexLoading ? (
                <span className="inline-block h-3 w-10 animate-pulse rounded bg-sky-500/20" />
              ) : (
                <span
                  className={`text-sm font-bold tabular-nums ${toneClass(
                    item?.return_pct,
                  )}`}
                >
                  {formatPct(item?.return_pct ?? null)}
                </span>
              )}
            </span>
          ))}
        </div>
      </Segment>

      {/* Temporarily hidden until Abillion strategy returns are available.
      <Segment
        icon={BarChart3}
        iconClass="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        label="Abillion's Performance"
      >
        <span className="text-lg font-bold leading-tight text-gray-400 dark:text-gray-500">
          —
        </span>
      </Segment>
      */}

      <Segment
        icon={Activity}
        iconClass="bg-blue-500/15 text-blue-600 dark:text-blue-400"
        label="No of Unique Stocks"
      >
        <span className="text-lg font-bold leading-tight tabular-nums text-gray-900 dark:text-white">
          {header?.no_of_stock ?? 0}
        </span>
      </Segment>

      <Segment
        icon={TrendingUp}
        iconClass="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        label="Avg. Peak Return %"
      >
        <span
          className={`text-lg font-bold leading-tight tabular-nums ${toneClass(
            header?.avg_peak_return,
          )}`}
        >
          {formatPct(header?.avg_peak_return)}
        </span>
      </Segment>

      <Segment
        icon={Coins}
        iconClass="bg-violet-500/15 text-violet-600 dark:text-violet-400"
        label="Avg. Current Return %"
      >
        <span
          className={`text-lg font-bold leading-tight tabular-nums ${toneClass(
            header?.avg_current_return,
          )}`}
        >
          {formatPct(header?.avg_current_return)}
        </span>
      </Segment>
    </div>
  );
};

export default PerformanceStats;
